import { inject, observer } from 'mobx-react';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { v4 } from 'uuid';
import C from '../../colors';
import { IMAGE_ACCEPT, isRawImageFile, isSupportedImageFile } from '../../imageUploadPolicy';
import { saveDirectorySource, saveFileSources } from '../../localOriginalSources';

const FileUploaderBase = observer(forwardRef(({
  size, onStartUpload, onUploadProgress, onUploaded, pictureStore, userStore, snackbarStore,
  albumId
}, ref) => {
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const sourceByFile = useRef(new WeakMap());

  const beforeUnload = (e) => { e.returnValue = '上传未完成，确定离开？'; return e.returnValue; };

  const persistUploadedSources = async (bindings) => {
    if (!albumId || bindings.length === 0) return;
    const directoryGroups = new Map();
    const fileBindings = [];
    bindings.forEach(binding => {
      if (binding.source.kind === 'file') {
        fileBindings.push({
          pictureId: binding.pictureId,
          fileName: binding.fileName,
          fileHandle: binding.source.handle,
        });
        return;
      }
      const existing = directoryGroups.get(binding.source.handle) || [];
      existing.push({ pictureId: binding.pictureId, fileName: binding.fileName });
      directoryGroups.set(binding.source.handle, existing);
    });
    await Promise.all([
      ...[...directoryGroups.entries()].map(([directoryHandle, sourceBindings]) => (
        saveDirectorySource({
          albumId,
          directoryHandle,
          bindings: sourceBindings,
          oneClickEligible: true,
        })
      )),
      saveFileSources({ albumId, bindings: fileBindings, oneClickEligible: true }),
    ]);
  };

  const findUploadedPicture = (originalFileName) => pictureStore.pictures.find(picture => (
    String(picture?.originalFileName || picture?.fileName || '').trim() === originalFileName
  ));

  const uploadFiles = async (files, { getSourceFolderName = () => null } = {}) => {
    if (!userStore.canUseRaw && files.some(isRawImageFile)) {
      snackbarStore.showSnackbar('RAW 文件解析为高级会员功能，请升级后使用', 'error');
      return;
    }
    onStartUpload(files.length);
    window.addEventListener('beforeunload', beforeUnload);
    let successFileCount = 0, existFileCount = 0, failFileCount = 0;
    const failedFileNames = [], failedFiles = [];
    const localSourceBindings = [];
    const reportProgress = () => onUploadProgress?.({
      pictureNum: files.length,
      completedFileCount: successFileCount + existFileCount + failFileCount,
      successFileCount,
      existFileCount,
      failFileCount,
      failedFileNames: [...failedFileNames],
      failedFiles: [...failedFiles],
    });
    try {
      for (const file of files) {
        const uuid = v4();
        const originalFileName = file.name.trim();
        // Detect files already visible (or currently uploading) before adding a
        // progress tile. Inserting and immediately removing that tile makes the
        // photo grid visibly jump for duplicate uploads.
        if (pictureStore.hasPictureNamed(originalFileName)) {
          existFileCount++;
          const source = sourceByFile.current.get(file);
          const picture = findUploadedPicture(originalFileName);
          if (source?.handle && picture?.id) {
            localSourceBindings.push({ pictureId: picture.id, fileName: originalFileName, source });
          }
          reportProgress();
          continue;
        }
        let status = 'fail';
        try {
          const uploadResult = await pictureStore.addPicture(
            file,
            uuid,
            albumId,
            // Store photos at the plan's highest customer-view size; each
            // order then shows them at its own (equal or smaller) size.
            Math.max(userStore.previewResolution?.max || 0, userStore.maxDimension || 0) || undefined,
            getSourceFolderName(file)
          );
          status = typeof uploadResult === 'string' ? uploadResult : uploadResult?.status;
        } catch (uploadError) {
          setError({ message: uploadError.message || '图片上传失败', name: file.name });
        }
        if (status === 'success' || status === 'exist') {
          if (status === 'success') successFileCount++;
          else existFileCount++;
          const source = sourceByFile.current.get(file);
          const picture = findUploadedPicture(originalFileName);
          if (source?.handle && picture?.id) {
            localSourceBindings.push({ pictureId: picture.id, fileName: originalFileName, source });
          }
        }
        else {
          failFileCount++;
          failedFileNames.push(originalFileName);
          failedFiles.push(file);
        }
        reportProgress();
      }
      try {
        await persistUploadedSources(localSourceBindings);
      } catch (_) {
        snackbarStore.showSnackbar('照片已上传，但未能保存本地原片关联；导出时需要重新关联原片文件夹', 'warning');
      }
      onUploaded({ pictureNum: files.length, successFileCount, existFileCount, failFileCount, failedFileNames, failedFiles });
    } finally {
      window.removeEventListener('beforeunload', beforeUnload);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    await uploadFiles(files);
  };

  const openFilePicker = async () => {
    if (!window.showOpenFilePicker) {
      fileInputRef.current?.click();
      return;
    }
    try {
      const handles = await window.showOpenFilePicker({ multiple: true });
      const selected = await Promise.all(handles.map(async handle => ({ handle, file: await handle.getFile() })));
      const supported = selected.filter(item => isSupportedImageFile(item.file));
      if (supported.length !== selected.length) {
        snackbarStore.showSnackbar(`已忽略 ${selected.length - supported.length} 个非图片文件`);
      }
      if (!supported.length) {
        snackbarStore.showSnackbar('没有可上传的图片', 'error');
        return;
      }
      supported.forEach(({ file, handle }) => {
        sourceByFile.current.set(file, { kind: 'file', handle });
      });
      await uploadFiles(supported.map(item => item.file));
    } catch (pickerError) {
      if (pickerError?.name !== 'AbortError') {
        snackbarStore.showSnackbar('打开本地照片失败', 'warning');
      }
    }
  };

  const handleFolderChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    e.target.value = '';
    const files = selectedFiles.filter(file => {
      const parts = (file.webkitRelativePath || file.name).split('/').filter(Boolean);
      return parts.length <= 2 && isSupportedImageFile(file);
    });
    const ignoredCount = selectedFiles.length - files.length;
    if (ignoredCount > 0) {
      snackbarStore.showSnackbar(`已忽略 ${ignoredCount} 个子目录或非图片文件`);
    }
    if (!files.length) {
      snackbarStore.showSnackbar('所选文件夹第一层没有可导入的图片', 'error');
      return;
    }
    await uploadFiles(files, {
      getSourceFolderName: file => {
        const parts = String(file.webkitRelativePath || '').split('/').filter(Boolean);
        return parts.length >= 2 ? parts[0].trim() || null : null;
      },
    });
  };

  const openFolderPicker = async () => {
    if (!window.showDirectoryPicker) {
      folderInputRef.current?.click();
      return;
    }
    try {
      const directoryHandle = await window.showDirectoryPicker({ mode: 'read' });
      const files = [];
      let ignoredCount = 0;
      for await (const entry of directoryHandle.values()) {
        if (entry.kind !== 'file') {
          ignoredCount += 1;
          continue;
        }
        const file = await entry.getFile();
        if (!isSupportedImageFile(file)) {
          ignoredCount += 1;
          continue;
        }
        sourceByFile.current.set(file, { kind: 'directory', handle: directoryHandle });
        files.push(file);
      }
      if (ignoredCount > 0) {
        snackbarStore.showSnackbar(`已忽略 ${ignoredCount} 个子目录或非图片文件`);
      }
      if (!files.length) {
        snackbarStore.showSnackbar('所选文件夹第一层没有可导入的图片', 'error');
        return;
      }
      await uploadFiles(files, { getSourceFolderName: () => directoryHandle.name || null });
    } catch (pickerError) {
      if (pickerError?.name !== 'AbortError') {
        snackbarStore.showSnackbar('打开本地文件夹失败', 'warning');
      }
    }
  };

  useImperativeHandle(ref, () => ({ retryFiles: uploadFiles }));

  const isSmall = size === 'small';
  return (
    <div>
      {error && (
        <div data-testid="file-upload-error" style={{ marginBottom: 8, padding: '8px 12px', background: C.dangerBg, borderRadius: 8, fontSize: 12, color: C.dangerFg }}>
          {error.message} — {error.name}
          <button onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: C.dangerFg }}>×</button>
        </div>
      )}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={openFilePicker} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isSmall ? '7px 14px' : '9px 18px',
          borderRadius: 8, border: 'none',
          background: C.ink, color: '#fff',
          fontFamily: C.sans, fontSize: isSmall ? 12 : 13, fontWeight: 500,
          cursor: 'pointer', boxShadow: '0 1px 0 rgba(0,0,0,.2)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          上传照片
        </button>
        <input ref={fileInputRef} data-testid="photo-upload-input" type="file" accept={IMAGE_ACCEPT} multiple hidden onChange={handleFileChange}/>
        <button type="button" onClick={openFolderPicker} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isSmall ? '6px 13px' : '8px 17px',
          borderRadius: 8, border: `1px solid ${C.hair2}`,
          background: '#fff', color: C.ink2,
          fontFamily: C.sans, fontSize: isSmall ? 12 : 13, fontWeight: 500,
          cursor: 'pointer',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7.5h7l2 2h9v9.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M3 7.5V5a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2.5"/>
          </svg>
          选择原片文件夹
        </button>
        <input ref={folderInputRef} data-testid="folder-upload-input" type="file" multiple webkitdirectory="" directory="" hidden onChange={handleFolderChange}/>
      </div>
    </div>
  );
}));

const FileUploader = inject('pictureStore','userStore','snackbarStore')(FileUploaderBase);

export { FileUploaderBase };
export default FileUploader;
